import { clsx, type ClassValue } from 'clsx'

export const cn = (...inputs: ClassValue[]) => clsx(inputs)

// The server decides deadlines, so the UI keeps its clock in step with the API's (`serverTime` from /api/me).
// That also makes the API's PORTAL_FAKE_NOW time travel apply to the whole UI in local demos.
let serverOffsetMs = 0
export const syncServerTime = (serverIso: string) => {
  serverOffsetMs = new Date(serverIso).getTime() - Date.now()
}

/** Current time, in step with the server. */
export const now = () => new Date(Date.now() + serverOffsetMs)

export const isPast = (iso?: string | null) => !!iso && new Date(iso).getTime() <= now().getTime()

export const formatDate = (iso: string, opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }) =>
  new Date(iso).toLocaleDateString('en-US', opts)

export const formatShortDate = (iso: string) => formatDate(iso, { month: 'short', day: 'numeric', year: 'numeric' })

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })

export const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

export const formatClock = (totalSec: number) => {
  const s = Math.max(0, Math.round(totalSec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

export const firstName = (name: string) => name.split(' ')[0]

/** New ids for nested items (materials, questions, options…) are UUIDs the server keeps. */
export const newId = () => crypto.randomUUID()

/** ISO instant → value for <input type="datetime-local"> in the browser's time zone. */
export const toLocalInput = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

/** <input type="datetime-local"> value → ISO instant. */
export const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null)

export const pluralize = (n: number, word: string, plural = `${word}s`) => `${n} ${n === 1 ? word : plural}`
